/*!
 * Black Dashboard PRO React - v1.0.0
 * Product Page: https://www.creative-tim.com/product/black-dashboard-pro-react
 * Copyright 2019 Creative Tim (https://www.creative-tim.com)
 * Coded by Creative Tim
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */
import React from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Table,
  Input,
  Label,
  Row,
  Col,
  Collapse,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import axios from 'axios';
import NotificationAlert from 'react-notification-alert';
import { Switch, Route, Link } from 'react-router-dom';

import CreateMerchantWizard from './forms/CreateMerchantWizard';
import CreateDriverWizard from './forms/CreateDriverWizard';
import User from '../components/Profiles/User';
import config from '../config';
import { isDashboardLocalDebugMode } from '../utils/environmentMode';

import isAuthenticated from '../Auth/isAuthenticated';
import * as user_urls from '../urls/api-users/admin';
import PaginationComponent from '../components/SharedComponents/PaginationComponent';
import { buildChangedUserEditPayload, getUserEditResponsePayload, validateUserEditResponse } from '../utils/userEditResponse';

class ExtendedTables extends React.Component {
  constructor(props) {
    super(props);
    this.notificationAlertRef = React.createRef();
    this.state = {
      showForm: false,
      form: '',
      validToken: false,
      users: [],
      shownUsers: [],
      modal: false,
      userId: null,
      username: null,
      firstname: null,
      lastname: null,
      email: null,
      phone_number: null,
      dob: null,
      oldFirstName: null,
      oldLastName: null,
      oldEmail: null,
      oldPhoneNumber: null,
      oldDob: null,
      currentPage: 1,
      totalPages: 1,
      pageSize: 50, // Number of rows per page
      roleFilter: null,
      searchQuery: ''
    };
  }

  handleFirstName = e => {
    this.setState({
      firstname: e.target.value
    });
  };

  handleLastName = e => {
    this.setState({
      lastname: e.target.value
    });
  };

  handlePhone = e => {
    this.setState({
      phone_number: e.target.value
    });
  };

  handleEmail = e => {
    this.setState({
      email: e.target.value
    });
  };

  handleDOB = e => {
    this.setState({
      dob: e.target.value
    });
  };

  resetPassword = userEmail => {
    // Function: Send a user a password reset email

    this.setState({ modal: false });
    axios({
      method: 'POST',
      url: 'https://' + config.config.AUTH_CONFIG.domain + '/dbconnections/change_password',
      headers: { 'content-type': 'application/json' },
      data: {
        client_id: config.config.AUTH_CONFIG.clientId,
        email: userEmail,
        connection: 'Username-Password-Authentication'
      }
    })
      .then(response => {
        this.notify('A reset password prompt has been emailed to ' + userEmail);
      })
      .catch(() => {
        this.notify('Unable to send password reset email right now.');
      });
  };

  toggle = () => {
    this.setState({ modal: !this.state.modal });
  };

  applyEditedUserToState = editedUser => {
    if (!editedUser || !editedUser.auth0_id) {
      return;
    }

    const mergeUser = user => {
      if (!user || user.auth0_id !== editedUser.auth0_id) {
        return user;
      }

      return {
        ...user,
        firstname: editedUser.firstname,
        lastname: editedUser.lastname,
        email: editedUser.email,
        phone: editedUser.phone,
      };
    };

    this.setState(currentState => ({
      users: (currentState.users || []).map(mergeUser),
      shownUsers: (currentState.shownUsers || []).map(mergeUser),
      oldFirstName: editedUser.firstname,
      oldLastName: editedUser.lastname,
      oldEmail: editedUser.email,
      oldPhoneNumber: editedUser.phone,
      firstname: editedUser.firstname,
      lastname: editedUser.lastname,
      email: editedUser.email,
      phone_number: editedUser.phone,
    }));
  };

  toggleForm = form => {
    this.setState({ showForm: !this.state.showForm, form: form });
  };

  handleClick = event => {
    this.setState({ activeTab: event.target.name });
  };

  filterRole = roleFilter => {
    // Reset the page to 1 when the filter is applied
    this.setState({ currentPage: 1, roleFilter: roleFilter }, () => {
      // Call fetchAllUsers with the updated role filter
      this.fetchAllUsers(1, roleFilter);
    });
  };

  handleSearchChange = event => {
    const { users, pageSize } = this.state; // Use the full dataset for filtering
    const val = event.target.value.trim(); // Trim the input to handle extra spaces
    this.setState({ searchQuery: val });

    if (!val) {
      // Reset to show all users and recalculate pagination
      const totalPages = Math.ceil(users.length / pageSize);
      this.setState({
        shownUsers: users.slice(0, pageSize), // Show the first page of all users
        currentPage: 1,
        totalPages
      });
    } else {
      // Filter based on the search value
      const filtered = users.filter(element => {
        const name = `${element.firstname} ${element.lastname}`;
        return (
          element.username.toLowerCase().includes(val.toLowerCase()) || element.email.toLowerCase().includes(val.toLowerCase()) || name.toLowerCase().includes(val.toLowerCase())
        );
      });

      // Update the pagination based on the filtered results
      const totalPages = Math.ceil(filtered.length / pageSize);
      this.setState({
        shownUsers: filtered.slice(0, pageSize), // Show the first page of the filtered results
        currentPage: 1, // Reset to the first page for filtered results
        totalPages
      });
    }
  };

  notify = message => {
    if (message === 'Successfully added merchant' || message === 'Successfully added driver') {
      this.fetchAllUsers();
    }
    var options = {};
    options = {
      place: 'tc',
      message: (
        <div>
          <div>{message}</div>
        </div>
      ),
      // type: type,
      // icon: icon,
      autoDismiss: 7
    };
    if (this.notificationAlertRef.current) {
      this.notificationAlertRef.current.notificationAlert(options);
    }
  };

  componentDidMount() {
    if (!isAuthenticated()) {
      this.props.history.push('/');
    }

    if (this.props.role === 0 || this.props.role === 100) {
      this.setState({ validToken: true });
      this.fetchAllUsers();
    } else {
      this.setState({ validToken: false });
      this.props.history.push('/');
    }
  }

  fetchAllUsers = (page = 1, roleFilter = null) => {
    const { pageSize } = this.state;
    const isLocalDashboardEnvironment = isDashboardLocalDebugMode();
    const usersQueryHost = user_urls.hasuraHost;

    const applyFetchedUsers = users => {
      const totalCount = users.length;
      const totalPages = Math.ceil(totalCount / pageSize) || 1;

      this.setState(
        {
          users,
          shownUsers: users,
          currentPage: page,
          totalPages,
          totalCount
        },
        () => {
          this.applyFilters(roleFilter, this.state.searchQuery);
        }
      );
    };

    const fetchDriverFallbackUsers = () => {
      const fallbackUrl = user_urls.dispatchDriversList([]);
      if (!fallbackUrl) {
        this.notify('Unable to load fallback driver users.');
        return Promise.resolve([]);
      }

      return axios({
        method: 'get',
        url: fallbackUrl,
        withCredentials: true,
        headers: {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('access_token')
        }
      })
        .then(driverResponse => {
          const payload = driverResponse.data && Array.isArray(driverResponse.data.payload) ? driverResponse.data.payload : [];
          return payload.map(driver => ({
            auth0_id: driver.auth0_id,
            username: driver.User && driver.User.username ? driver.User.username : '',
            firstname: driver.User && driver.User.firstname ? driver.User.firstname : '',
            lastname: driver.User && driver.User.lastname ? driver.User.lastname : '',
            email: driver.User && driver.User.email ? driver.User.email : '',
            phone: driver.phone || (driver.User && driver.User.phone) || '',
            Roles: [{ role_description: 'driver', role: 300 }]
          }));
        })
        .catch(() => {
          this.notify('Unable to load fallback driver users.');
          return [];
        });
    };

    const fetchLocalUsers = () => {
      if (!isLocalDashboardEnvironment) {
        return Promise.resolve([]);
      }

      return axios({
        method: 'get',
        url: user_urls.localUsersList(roleFilter),
        withCredentials: true,
        headers: {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('access_token')
        }
      })
        .then(localResponse => (localResponse.data && Array.isArray(localResponse.data.payload) ? localResponse.data.payload : []))
        .catch(() => []);
    };

    const mergeUsersByAuth0Id = (...userGroups) => {
      const mergedUsersById = new Map();
      const preferIncomingValue = (existingValue, incomingValue) => {
        if (incomingValue === null || incomingValue === undefined) {
          return existingValue || '';
        }

        if (typeof incomingValue === 'string') {
          return incomingValue.trim() !== '' ? incomingValue : (existingValue || '');
        }

        return incomingValue;
      };

      userGroups.flat().forEach(user => {
        if (!user) {
          return;
        }

        const identifier = user.auth0_id || user.email || `${user.firstname || ''}-${user.lastname || ''}`;
        const existingUser = mergedUsersById.get(identifier);

        if (!existingUser) {
          mergedUsersById.set(identifier, user);
          return;
        }

        const existingRoles = Array.isArray(existingUser.Roles) ? existingUser.Roles : [];
        const incomingRoles = Array.isArray(user.Roles) ? user.Roles : [];
        const mergedRoles = [...existingRoles];
        const seenRoles = new Set(existingRoles.map(role => role.role));

        incomingRoles.forEach(role => {
          if (!seenRoles.has(role.role)) {
            mergedRoles.push(role);
            seenRoles.add(role.role);
          }
        });

        mergedUsersById.set(identifier, {
          ...existingUser,
          ...user,
          username: preferIncomingValue(existingUser.username, user.username),
          firstname: preferIncomingValue(existingUser.firstname, user.firstname),
          lastname: preferIncomingValue(existingUser.lastname, user.lastname),
          email: preferIncomingValue(existingUser.email, user.email),
          phone: preferIncomingValue(existingUser.phone, user.phone),
          Roles: mergedRoles
        });
      });

      return Array.from(mergedUsersById.values());
    };

    // Construct the base query to fetch all users without pagination
    let query = `
  query {
    Users {
      auth0_id
      username
      firstname
      lastname
      email
      phone
      Roles {
        role_description
        role
      }
    }
    Users_aggregate {
      aggregate {
        count
      }
    }
  }
`;

    // Add a condition to the query if a role filter is provided
    if (roleFilter !== null) {
      query = `
    query {
      Users(where: {Roles: {role: {_eq: ${roleFilter}}}}) {
        auth0_id
        username
        firstname
        lastname
        email
        phone
        Roles {
          role_description
          role
        }
      }
      Users_aggregate(where: {Roles: {role: {_eq: ${roleFilter}}}}) {
        aggregate {
          count
        }
      }
    }
  `;
    }

    const QUERY = { query };

    return axios({
      method: 'post',
      url: usersQueryHost,
      withCredentials: true,
      data: QUERY,
      headers: {
        'content-type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token')
      }
    })
      .then(response => {
        if (response.data.errors) {
          if (response.data.errors.length > 0) {
            if (response.data.errors[0].message === 'Malformed Authorization header' || response.data.errors[0].extensions.code === 'invalid-jwt') {
              this.props.history.push('/logout');
            }
          }
        }

        if (response.data.data) {
          const users = response.data.data.Users;
          const extraDriverUsers = roleFilter === 300 ? fetchDriverFallbackUsers() : Promise.resolve([]);

          if (isLocalDashboardEnvironment) {
            fetchLocalUsers().then(localUsers => {
              Promise.resolve(extraDriverUsers).then(fallbackUsers => {
                const mergedUsers = mergeUsersByAuth0Id(users, localUsers, fallbackUsers);
                applyFetchedUsers(mergedUsers);
              });
            });
            return;
          }

          Promise.resolve(extraDriverUsers).then(fallbackUsers => {
            const mergedUsers = mergeUsersByAuth0Id(users, fallbackUsers);
            applyFetchedUsers(mergedUsers);
          });
        }
      })
      .catch(() => {
        if (isLocalDashboardEnvironment) {
          return fetchLocalUsers().then(localUsers => {
            if (roleFilter === 300) {
              return fetchDriverFallbackUsers().then(fallbackUsers => {
                const mergedUsers = mergeUsersByAuth0Id(localUsers, fallbackUsers);
                applyFetchedUsers(mergedUsers);
                return mergedUsers;
              });
            }

            applyFetchedUsers(localUsers);
            return localUsers;
          });
        }

        if (roleFilter === 300) {
          return fetchDriverFallbackUsers().then(fallbackUsers => {
            applyFetchedUsers(fallbackUsers);
            return fallbackUsers;
          });
        }

        this.notify('Unable to load users right now.');
        return [];
      });
  };

  // Apply filters (role and search)
  applyFilters = (role, query) => {
    let filtered = this.state.users;

    // Apply role filter
    if (role) {
      filtered = filtered.filter(user => user.Roles.some(roleObj => roleObj.role === role));
    }

    // Apply search query filter
    if (query) {
      filtered = filtered.filter(
        user =>
          user.username.toLowerCase().includes(query.toLowerCase()) ||
          user.firstname.toLowerCase().includes(query.toLowerCase()) ||
          user.lastname.toLowerCase().includes(query.toLowerCase())
      );
    }

    // Paginate filtered users
    this.setState({
      shownUsers: filtered.slice((this.state.currentPage - 1) * this.state.pageSize, this.state.currentPage * this.state.pageSize),
      totalPages: Math.ceil(filtered.length / this.state.pageSize)
    });
  };

  handlePageChange = page => {
    const { roleFilter } = this.state;
    this.setState({ currentPage: page }, () => {
      this.fetchAllUsers(page, roleFilter);
    });
  };

  editUser = () => {
    const data = buildChangedUserEditPayload(this.state);

    if (Object.keys(data).length === 0) {
      this.notify('No changes to save.');
      return;
    }

    axios({
      data: data,
      method: 'put',
      url: user_urls.editUser + this.state.userId,
      withCredentials: true,
      headers: { 'content-type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('access_token') }
    })
      .then(response => {
        const validationMessage = validateUserEditResponse(response, data);

        if (validationMessage) {
          this.notify(validationMessage);
          return null;
        }

        const editedUser = getUserEditResponsePayload(response);
        this.applyEditedUserToState(editedUser);

        const refreshPromise = isDashboardLocalDebugMode() ? Promise.resolve() : this.fetchAllUsers();

        return refreshPromise.then(() => {
        this.toggle();
        this.reset();
        this.notify('Successfully edited user info');
        });
      })
      .catch(() => {
        this.notify('Unable to update user information.');
      });
  };

  reset = () => {
    this.setState({
      firstname: null,
      lastname: null,
      email: null,
      phone_number: null,
      dob: null
    });
  };

  render() {
    return (
      this.state.validToken && (
        <Switch>
          <Route path="/admin/users/:userId" component={User} />
          <Route exact path="/admin/users">
            <>
              <div className="rna-container">
                <NotificationAlert ref={this.notificationAlertRef} />
              </div>
              <div className="content">
                <Row>
                  <Col>
                    <div className="float-right">
                      <UncontrolledDropdown style={{ marginRight: 20, marginTop: 5, marginBottom: 20 }}>
                        {!this.state.showForm ? (
                          <DropdownToggle caret={false} className="btn btn-success" color="default" data-toggle="dropdown" type="button">
                            <div>
                              <i className="tim-icons icon-simple-add" style={{ marginRight: 10 }} />
                              Add User
                            </div>
                          </DropdownToggle>
                        ) : (
                          <Button
                            className="btn btn-danger"
                            onClick={() => {
                              this.toggleForm();
                            }}
                          >
                            <div>
                              <i className="tim-icons icon-simple-remove" style={{ marginRight: 10 }} />
                              Cancel
                            </div>
                          </Button>
                        )}
                        {!this.state.showForm && (
                          <DropdownMenu right>
                            <DropdownItem onClick={() => this.toggleForm('merchant')}>Merchant</DropdownItem>
                            <DropdownItem onClick={() => this.toggleForm('driver')}>Driver</DropdownItem>
                          </DropdownMenu>
                        )}
                      </UncontrolledDropdown>
                    </div>
                    <Card className="card-form">
                      <Collapse isOpen={this.state.showForm}>
                        {this.state.form === 'merchant' && <CreateMerchantWizard toggleDropdown={this.toggleForm} notify={this.notify}></CreateMerchantWizard>}
                        {this.state.form === 'driver' && <CreateDriverWizard toggleDropdown={this.toggleForm} notify={this.notify}></CreateDriverWizard>}
                      </Collapse>
                    </Card>
                  </Col>
                </Row>
                <Row style={{ marginTop: 20 }}>
                  <Col md="12">
                    <Card>
                      <CardHeader>
                        <div className="tools float-right">
                          <Row>
                            <Col md="19">
                              <UncontrolledDropdown style={{ marginRight: 20, marginTop: 5 }}>
                                <DropdownToggle caret className="btn-link btn-icon" color="default" data-toggle="dropdown" type="button">
                                  <i className="fas fa-filter"></i>
                                </DropdownToggle>
                                <DropdownMenu right>
                                  <DropdownItem className="text-success" onClick={() => this.filterRole(null)}>
                                    All
                                  </DropdownItem>
                                  <DropdownItem onClick={() => this.filterRole(0)}>Super Admins</DropdownItem>
                                  <DropdownItem onClick={() => this.filterRole(100)}>Admins</DropdownItem>
                                  <DropdownItem onClick={() => this.filterRole(200)}>Merchants</DropdownItem>
                                  <DropdownItem onClick={() => this.filterRole(300)}>Drivers</DropdownItem>
                                  <DropdownItem onClick={() => this.filterRole(400)}>Customers</DropdownItem>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </Col>
                            <Col>
                              <Input placeholder="Search users..." value={this.state.searchQuery} onChange={this.handleSearchChange}></Input>
                            </Col>
                          </Row>
                        </div>
                        <CardTitle tag="h3">
                          <i className="tim-icons text-primary" /> Users
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <Table>
                          <thead className="text-primary">
                            <tr>
                              <th>Auth0-ID</th>
                              <th>Username</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Primary Phone</th>
                              <th>Role</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {this.state.shownUsers.map((user, index) => {
                              const roleDescription = user.Roles && user.Roles.length > 0 ? user.Roles[0].role_description : 'No role';
                              return (
                                <tr key={index}>
                                  <td>{user.auth0_id}</td>
                                  <td>{user.username}</td>
                                  <td>
                                    {user.firstname} {user.lastname}
                                  </td>
                                  <td>{user.email}</td>
                                  <td>{user.phone}</td>
                                  <td>{roleDescription}</td>
                                  <td>
                                    <i
                                      className="tim-icons icon-pencil"
                                      style={{ cursor: 'pointer', fontSize: '1.5em', paddingRight: '10px' }}
                                      onClick={() => {
                                        this.setState({
                                          firstname: user.firstname,
                                          lastname: user.lastname,
                                          phone_number: user.phone,
                                          email: user.email,
                                          dob: user.dob,
                                          oldFirstName: user.firstname,
                                          oldLastName: user.lastname,
                                          oldPhoneNumber: user.phone,
                                          oldEmail: user.email,
                                          userId: user.auth0_id
                                        });
                                        this.toggle();
                                      }}
                                    />
                                    <Route path="/admin/users/:user_id" component={User} />
                                    <Link to={'/admin/users/' + encodeURIComponent(user.auth0_id || user.email || '')}>
                                      <i className="tim-icons icon-badge" style={{ fontSize: '1.5em', cursor: 'pointer', color: '#cdcaca', marginLeft: '10px' }} />
                                    </Link>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        {/* Add Pagination Component here */}
                        <PaginationComponent currentPage={this.state.currentPage} totalPages={this.state.totalPages} onPageChange={this.handlePageChange} />
                      </CardBody>
                    </Card>
                    <Modal isOpen={this.state.modal} toggle={this.toggle} style={{ color: `black` }}>
                      <ModalHeader>
                        <strong>Edit User Info</strong>
                      </ModalHeader>
                      <ModalBody>
                        <Label>First Name</Label>
                        <Input required type="text" style={{ color: `black` }} onChange={e => this.handleFirstName(e)} value={this.state.firstname} />
                        <Label>Last Name</Label>
                        <Input required type="text" style={{ color: `black` }} onChange={e => this.handleLastName(e)} value={this.state.lastname} />
                        <Label>Email</Label>
                        <Input required type="email" style={{ color: `black` }} onChange={e => this.handleEmail(e)} value={this.state.email} />
                        <Label>Phone Number</Label>
                        <Input required type="phone" style={{ color: `black` }} onChange={e => this.handlePhone(e)} value={this.state.phone_number} />
                        <div
                          style={{
                            textAlign: 'center',
                            marginTop: '5%',
                            color: '#EA2835',
                            cursor: 'pointer'
                          }}
                          onClick={() => this.resetPassword(this.state.oldEmail)}
                        >
                          Send password reset email to {this.state.oldEmail}
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="danger"
                          onClick={() => {
                            this.toggle();
                            this.reset();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => {
                            this.editUser();
                          }}
                        >
                          Save
                        </Button>
                      </ModalFooter>
                    </Modal>
                    {/* </Card> */}
                  </Col>
                </Row>
              </div>
            </>
          </Route>
        </Switch>
      )
    );
  }
}

export default ExtendedTables;
